/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { domainAPI } from "../../../environments/environment";
import { IResponseModel } from "../../../servers/database/models/response.model";
import {
  IMasterChannel,
  IMasterChannelEnr,
  IMasterPosition,
  IBlobItem,
  IMasterCatalogType,
  IEffective,
  IEffectiveGroup,
  IEffectiveGroupCreateBody,
  IEffectiveGroupUpdateBody,
  IEffectiveSaveBody,
  IMasterEffective,
  IMasterPermission,
  IMasterResource,
  IMasterScope,
  IScopeAssignResult,
  IScopeUser,
  ScopeAssignAction,
  IMasterNetworkType,
  IMasterLanguage,
  IMasterSyncField,
  IMasterAutoEnrichField,
  IAutoEnrichFieldSaveBody,
  ISyncFieldSaveBody,
  ISyncPathCheck,
  ISyncPathFields,
  IPropertySourceName,
  AutoEnrichSourceType,
  AutoEnrichDocumentLevel,
  IMasterPersona,
  IPersona,
  IPersonaSaveBody,
} from "../models/master.model";
import { FormDisplayItem } from "@app/feature/management/banner-management/models/banner-api.model";
import { FeatureFlagService } from "./feature-flag.service";
import { BannerCfgChannel } from "@app/feature/settings/authorizations/master-data/master-data-bn-config/master-data-bn-config-channel/models/banner-cfg-channel.model";
import { SegmentItem } from "@app/feature/settings/authorizations/master-data/master-data-bn-config/master-data-bn-config-segment/master-data-bn-config-segment";
import { NtypeItem } from "@app/feature/settings/authorizations/master-data/master-data-bn-config/master-data-bn-config-ntype/master-data-bn-config-ntype";
import { PositionScopeItem } from "@app/feature/settings/authorizations/master-data/master-data-bn-config/master-data-bn-config-position-scope/master-data-bn-config-position-scope";
import { CFGFormItem } from "@app/feature/settings/authorizations/master-data/master-data-bn-config/master-data-bn-config-cfg-form-component/master-data-bn-config-cfg-form-component";
import { AccessRequestItem } from "@app/feature/settings/authorizations/master-data/master-data-bn-config/master-data-bn-config-access-request/master-data-bn-config-access-request";
import {
  BannerCfgCampaign,
  BannerCfgCampaignInput,
  BannerCfgCampaignDeleteResult,
  BannerCfgCampaignQuery,
  BannerCfgCampaignListResponse,
  BANNER_CAMPAIGN_DEFAULT_LIMIT,
  BANNER_CAMPAIGN_MAX_LIMIT,
} from "@app/feature/settings/authorizations/master-data/master-data-bn-config/master-data-bn-cfg-campaigns/models/banner-cfg-campaign.model";

interface IAuditLogResponse extends IResponseModel {
  pagination: any;
}

/** Only these badge colors are selectable in the banner form; other blobs
 *  under `enricher/assets/badge_colour` are ignored. */
const BADGE_COLOR_ALLOWLIST = new Set([
  "exclusive",
  "flash-sale",
  "free-gift",
  "only-myais",
  "port-in-only",
  "postpaid",
  "prepaid",
  "students",
]);

@Injectable({
  providedIn: "root",
})
export class MastersService {
  private readonly http = inject(HttpClient);
  private readonly featureFlags = inject(FeatureFlagService);

  /** Banner-position CRUD base. Read per call, not once at construction, so
   *  a live change to `enableBannerConfig` reaches this root singleton. */
  private get bannerPositionUrl(): string {
    return (
      domainAPI.enricher +
      (this.featureFlags.get("enableBannerConfig")
        ? "/banner-configuration/position"
        : "/banner-position")
    );
  }

  private readonly apiUrl = {
    getProductOfferings: domainAPI.enricher + "/productOffering",
    getProductOfferingsPagination:
      domainAPI.enricher + "/shelf/productOffering/pagination",
    searchProductOfferings: domainAPI.enricher + "/shelf/search",
    // Category & Catalog
    getCategory: domainAPI.enricher + "/category",
    getCatalog: domainAPI.enricher + "/catalog",
    getCatalogType: domainAPI.enricher + "/master/catalogType",
    // Banner Position — list (with RBAC channel/scope filtering) stays on
    // banner-management; create/update/delete use the /banner-configuration/position
    // CRUD (see bannerPositionUrl — flag-dependent, so not in this table).
    getMasterPosition: domainAPI.enricher + "/banner-management/position",
    // Master Position (ENR_POSITION) update/delete
    updateAndDeleteMasterPosition:
      domainAPI.enricher + "/position/{positionId}",
    // Channel
    getMasterChannel: domainAPI.enricher + "/master/channel",
    // Banner Config Channel
    getAndCreateMasterBannerChannelNew:
      domainAPI.enricher + "/banner-management/channel",
    updateAndDeleteMasterBannerChannelNew:
      domainAPI.enricher + "/banner-management/channel/{channelId}",

    getAndCreateMasterChannelENR: domainAPI.enricher + "/channel",
    updateAndDeleteMasterChannelENR:
      domainAPI.enricher + "/channel/{channelId}",
    // Persona — same split as nType below: /master/persona is the
    // read-only lookup the pricing Customer-persona select reads, while
    // /persona is the paginated CRUD behind the Configuration →
    // Customer Persona tab.
    getMasterPersona: domainAPI.enricher + "/master/persona",
    getAndCreatePersona: domainAPI.enricher + "/persona",
    updateAndDeletePersona: domainAPI.enricher + "/persona/{personaId}",
    // Segment & Ntype
    getMasterSegment: domainAPI.enricher + "/master/segment",
    // Effective — same split as persona/nType: /master/effective is the
    // read-only lookup the enrichment editor reads, while /effective is the
    // paginated CRUD behind the Configuration → Effective tab.
    getMasterEffective: domainAPI.enricher + "/master/effective",
    getAndCreateEffective: domainAPI.enricher + "/effective",
    updateAndDeleteEffective: domainAPI.enricher + "/effective/{effectiveId}",
    // Grouped effective CRUD — one call per OPTION instead of one per
    // (option, language) row. The tab pages on /effective/grouped and writes
    // through /effective/option; the flat routes above stay for the row-level
    // fallback and the enrichment editor's lookup.
    getEffectiveGrouped: domainAPI.enricher + "/effective/grouped",
    createEffectiveOption: domainAPI.enricher + "/effective/option",
    updateAndDeleteEffectiveOption:
      domainAPI.enricher + "/effective/option/{option}",
    getMasterNtype: domainAPI.enricher + "/master/nType",
    getAndCreateNtype: domainAPI.enricher + "/nType",
    updateAndDeleteNtype: domainAPI.enricher + "/nType/{ntypeId}",
    //Blob
    getBlob: domainAPI.enricher + "/blob",
    getBlobPublicList: domainAPI.enricher + "/blob/public/list",
    // Permission
    getAndCreateMasterPermission: domainAPI.enricher + "/permission",
    updateAndDeleteMasterPermission:
      domainAPI.enricher + "/permission/{permissionId}",

    // Position
    getAndCreateMasterPosition: domainAPI.enricher + "/position",
    //Scopes
    getAndCreateMasterScope: domainAPI.enricher + "/scope",
    updateAndDeleteScopes: domainAPI.enricher + "/scope/{scopeId}",
    scopeUsers: domainAPI.enricher + "/scope/{scopeId}/users",
    scopeUserOptions: domainAPI.enricher + "/scope/user-options",
    assignScopeUsers: domainAPI.enricher + "/scope/users",
    //Resources
    getResources: domainAPI.enricher + "/resource",
    updateAndDeleteResource: domainAPI.enricher + "/resource/{resourceId}",
    //Languages — master catalog (ENR_MST_LANGUAGE)
    getAndCreateLanguage: domainAPI.enricher + "/language",
    updateAndDeleteLanguage: domainAPI.enricher + "/language/{languageId}",
    //Sync Fields — the Field select of Workflow › Sync Rules
    getAndCreateSyncField: domainAPI.enricher + "/sync-field",
    updateAndDeleteSyncField: domainAPI.enricher + "/sync-field/{fieldId}",
    syncFieldPathFields: domainAPI.enricher + "/sync-field/path-fields",
    syncFieldOrder: domainAPI.enricher + "/sync-field/order",
    syncFieldCheckPath: domainAPI.enricher + "/sync-field/check-path",
    //Auto Enrich Fields — Configuration › Access › Rule Property
    getAndCreateAutoEnrichField: domainAPI.enricher + "/auto-enrich-field",
    updateAndDeleteAutoEnrichField:
      domainAPI.enricher + "/auto-enrich-field/{fieldId}",
    autoEnrichFieldPathFields:
      domainAPI.enricher + "/auto-enrich-field/path-fields",
    autoEnrichFieldSourceNames:
      domainAPI.enricher + "/auto-enrich-field/source-names",
    autoEnrichFieldCheckSource:
      domainAPI.enricher + "/auto-enrich-field/check-source",
    //Audit Logs
    getAuditLog: domainAPI.enricher + "/auditlog",
    getAuditLogById: domainAPI.enricher + "/auditlog/{auditId}",
    //Master Form Component
    mstFormComponentEndPoint:
      domainAPI.enricher + "/banner-configuration/mstFormComponent",
    //Master Segment
    mstSegmentEndPoint: domainAPI.enricher + "/banner-configuration/mstSegment",
    //Master Ntype
    mstNTypeEndPoint: domainAPI.enricher + "/banner-configuration/mstNtype",
    //Config Position Scope
    cfgChannelEndPoint: domainAPI.enricher + "/banner-configuration/cfgChannel",
    cfgPositionScopeEndPoint:
      domainAPI.enricher + "/banner-configuration/cfgPositionScope",
    //Config Form Component
    cfgFormComponentEndPoint:
      domainAPI.enricher + "/banner-configuration/cfgFormComponent",
    //Access Request
    accessRequestEndPoint:
      domainAPI.enricher + "/banner-configuration/accessRequest",
    cfgCampaignEndPoint:
      domainAPI.enricher + "/banner-configuration/cfgCampaign",
  };

  getProductOffering(queryParams?: any): Observable<IResponseModel> {
    if (queryParams) {
      return this.http.get<IResponseModel>(this.apiUrl.getProductOfferings, {
        params: queryParams,
      });
    }

    return this.http.get<IResponseModel>(this.apiUrl.getProductOfferings);
  }

  getCategory(): Observable<IResponseModel> {
    return this.http.get<IResponseModel>(this.apiUrl.getCategory, {});
  }

  getCatalog(): Observable<IResponseModel> {
    return this.http.get<IResponseModel>(this.apiUrl.getCatalog, {});
  }

  // Banner Position
  getMasterPosition(
    channelId: string,
  ): Observable<IResponseModel<IMasterPosition[]>> {
    return this.http.get<IResponseModel<IMasterPosition[]>>(
      `${this.apiUrl.getMasterPosition}?channelId=${encodeURIComponent(channelId)}`,
    );
  }

  getMasterBannerPosition(params: any): Observable<IResponseModel<any[]>> {
    return this.http.get<IResponseModel<any[]>>(this.apiUrl.getMasterPosition, {
      params,
    });
  }

  createMasterBannerPosition(param: any): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(this.bannerPositionUrl, param);
  }

  updateMasterBannerPosition(
    id: string,
    param: any,
  ): Observable<IResponseModel<any>> {
    const url = `${this.bannerPositionUrl}/{positionId}`.replace(
      "{positionId}",
      encodeURIComponent(id),
    );
    return this.http.put<IResponseModel<any>>(url, param);
  }

  deleteMasterBannerPosition(id: string): Observable<IResponseModel<any>> {
    const url = `${this.bannerPositionUrl}/{positionId}`.replace(
      "{positionId}",
      encodeURIComponent(id),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  // Master Position
  getMasterPositionNew(queryParams?: any): Observable<IResponseModel<any>> {
    return this.http.get<IResponseModel<any>>(
      this.apiUrl.getAndCreateMasterPosition,
      { params: queryParams },
    );
  }

  createMasterPosition(param: any): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.getAndCreateMasterPosition,
      param,
    );
  }

  updateMasterPosition(
    positionId: string,
    param: any,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteMasterPosition}`.replace(
      "{positionId}",
      encodeURIComponent(positionId),
    );
    return this.http.put<IResponseModel<any>>(url, param);
  }

  deleteMasterPosition(positionId: string): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteMasterPosition}`.replace(
      "{positionId}",
      encodeURIComponent(positionId),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  // Channel
  getMasterChannel(): Observable<IResponseModel<IMasterChannel[]>> {
    return this.http.get<IResponseModel<IMasterChannel[]>>(
      this.apiUrl.getMasterChannel,
      {},
    );
  }

  getMasterChannelNew(
    queryParams?: any,
  ): Observable<IResponseModel<IMasterChannel[]>> {
    return this.http.get<IResponseModel<IMasterChannel[]>>(
      this.apiUrl.getAndCreateMasterBannerChannelNew,
      { params: queryParams },
    );
  }

  getMasterChannelENR(
    params: any,
  ): Observable<IResponseModel<IMasterChannelEnr[]>> {
    return this.http.get<IResponseModel<IMasterChannelEnr[]>>(
      this.apiUrl.getAndCreateMasterChannelENR,
      {
        params,
      },
    );
  }

  createMasterChannelENR(param: any): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.getAndCreateMasterChannelENR,
      param,
    );
  }

  updateMasterChannelENR(
    channelId: string,
    param: any,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteMasterChannelENR}`.replace(
      "{channelId}",
      encodeURIComponent(channelId),
    );
    return this.http.put<IResponseModel<any>>(url, param);
  }

  deleteMasterChannelENR(channelId: string): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteMasterChannelENR}`.replace(
      "{channelId}",
      encodeURIComponent(channelId),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  // Banner channel

  createBannerConfigChannel(param: any): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.getAndCreateMasterBannerChannelNew,
      param,
    );
  }

  updateBannerConfigChannel(
    channelId: string,
    param: any,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteMasterBannerChannelNew}`.replace(
      "{channelId}",
      encodeURIComponent(channelId),
    );
    return this.http.put<IResponseModel<any>>(url, param);
  }

  deleteBannerConfigChannel(
    channelId: string,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteMasterBannerChannelNew}`.replace(
      "{channelId}",
      encodeURIComponent(channelId),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  getMasterNtype(
    businessType?: string,
  ): Observable<IResponseModel<IMasterNetworkType[]>> {
    // BE filters the network-type master by `businessType` when provided;
    // omit the param to fetch the full list.
    const options = businessType
      ? { params: new HttpParams().set("businessType", businessType) }
      : {};
    return this.http.get<IResponseModel<IMasterNetworkType[]>>(
      this.apiUrl.getMasterNtype,
      options,
    );
  }

  // Ntype CRUD lives at /nType (no /master prefix). The /master/nType
  // endpoint is read-only and reused by other menus that just need the
  // option list.
  getNtype(
    queryParams?: any,
  ): Observable<IResponseModel<IMasterNetworkType[]>> {
    return this.http.get<IResponseModel<IMasterNetworkType[]>>(
      this.apiUrl.getAndCreateNtype,
      { params: queryParams },
    );
  }

  createNtype(param: any): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.getAndCreateNtype,
      param,
    );
  }

  updateNtype(
    ntypeId: string | number,
    param: any,
  ): Observable<IResponseModel<any>> {
    const url = this.apiUrl.updateAndDeleteNtype.replace(
      "{ntypeId}",
      encodeURIComponent(String(ntypeId)),
    );
    return this.http.put<IResponseModel<any>>(url, param);
  }

  deleteNtype(ntypeId: string | number): Observable<IResponseModel<any>> {
    const url = this.apiUrl.updateAndDeleteNtype.replace(
      "{ntypeId}",
      encodeURIComponent(String(ntypeId)),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  getMasterSegment(): Observable<IResponseModel<unknown[]>> {
    return this.http.get<IResponseModel<unknown[]>>(
      this.apiUrl.getMasterSegment,
      {},
    );
  }

  /** All personas (`GET /master/persona`), sorted by personaGroup on the
   *  BE — there is no active/inactive concept. Read-only lookup for the
   *  pricing Customer-persona select — rows are managed on the
   *  Configuration → Customer Persona tab via `/persona`. */
  getPersonas(): Observable<IResponseModel<IMasterPersona[]>> {
    return this.http.get<IResponseModel<IMasterPersona[]>>(
      this.apiUrl.getMasterPersona,
    );
  }

  /** Paginated persona CRUD list (`GET /persona?page&pageSize&search`).
   *  Persona CRUD lives at /persona with no /master prefix, exactly like
   *  the nType pair above. Distinct from {@link getPersonas}: that one
   *  reads the trimmed read-only lookup, this one returns the full row
   *  (integer PK + create audit) plus the `pagination` envelope the
   *  Configuration → Customer Persona tab pages on. */
  getPersonaList(
    queryParams?: Record<string, string | number>,
  ): Observable<IResponseModel<IPersona[]>> {
    return this.http.get<IResponseModel<IPersona[]>>(
      this.apiUrl.getAndCreatePersona,
      { params: queryParams },
    );
  }

  createPersona(param: IPersonaSaveBody): Observable<IResponseModel<IPersona>> {
    return this.http.post<IResponseModel<IPersona>>(
      this.apiUrl.getAndCreatePersona,
      param,
    );
  }

  updatePersona(
    personaId: string | number,
    param: IPersonaSaveBody,
  ): Observable<IResponseModel<IPersona>> {
    const url = this.apiUrl.updateAndDeletePersona.replace(
      "{personaId}",
      encodeURIComponent(String(personaId)),
    );
    return this.http.put<IResponseModel<IPersona>>(url, param);
  }

  /** Permanent — the BE hard-deletes the row; there is no undo and no
   *  soft-delete tombstone to restore from. */
  deletePersona(personaId: string | number): Observable<IResponseModel<null>> {
    const url = this.apiUrl.updateAndDeletePersona.replace(
      "{personaId}",
      encodeURIComponent(String(personaId)),
    );
    return this.http.delete<IResponseModel<null>>(url);
  }

  /** Effective-option master (`GET /master/effective`), localized by
   *  `languageId` (1 = TH, 2 = EN per the ENR_EFFECTIVE seed). Returns the
   *  productEffectiveOption codes + labels; the BE does not split prepaid /
   *  postpaid, so that stays a client-side filter. */
  getMasterEffective(
    languageId: number | string,
  ): Observable<IResponseModel<IMasterEffective[]>> {
    return this.http.get<IResponseModel<IMasterEffective[]>>(
      this.apiUrl.getMasterEffective,
      { params: new HttpParams().set("languageId", String(languageId)) },
    );
  }

  /** Paginated effective CRUD list (`GET /effective?search&page&pageSize&
   *  isDeleted&showDeleted&showInactive`). Effective CRUD lives at /effective
   *  with no /master prefix, exactly like the persona and nType pairs above.
   *  Distinct from {@link getMasterEffective}: that one reads the trimmed,
   *  language-filtered lookup the enrichment editor offers, this one returns
   *  the full row (USE_FLAG + create audit) plus the `pagination` envelope the
   *  Configuration → Effective tab pages on. `search` matches option, topic,
   *  description, languageId or id. USE_FLAG modes mirror nType: no flag →
   *  active only, `isDeleted` → tombstones only, `showDeleted` → every row,
   *  `showInactive` → everything except 'D'. */
  getEffectiveList(
    queryParams?: Record<string, string | number | boolean>,
  ): Observable<IResponseModel<IEffective[]>> {
    return this.http.get<IResponseModel<IEffective[]>>(
      this.apiUrl.getAndCreateEffective,
      { params: queryParams },
    );
  }

  createEffective(
    param: IEffectiveSaveBody,
  ): Observable<IResponseModel<IEffective>> {
    return this.http.post<IResponseModel<IEffective>>(
      this.apiUrl.getAndCreateEffective,
      param,
    );
  }

  updateEffective(
    effectiveId: string | number,
    param: IEffectiveSaveBody,
  ): Observable<IResponseModel<IEffective>> {
    const url = this.apiUrl.updateAndDeleteEffective.replace(
      "{effectiveId}",
      encodeURIComponent(String(effectiveId)),
    );
    return this.http.put<IResponseModel<IEffective>>(url, param);
  }

  /** Two-stage, like nType: the first DELETE flips USE_FLAG to 'D' (the row
   *  survives as a tombstone and can be restored by PUTting useFlag 'Y'),
   *  deleting an already-'D' row removes it from the table for good. */
  deleteEffective(
    effectiveId: string | number,
  ): Observable<IResponseModel<null>> {
    const url = this.apiUrl.updateAndDeleteEffective.replace(
      "{effectiveId}",
      encodeURIComponent(String(effectiveId)),
    );
    return this.http.delete<IResponseModel<null>>(url);
  }

  /** Option-grouped effective list (`GET /effective/grouped?search&page&
   *  pageSize&isDeleted&showDeleted&showInactive`). Same query modes as
   *  {@link getEffectiveList}, but each item is one OPTION carrying its
   *  `translations` array — and `pagination.totalItems` counts OPTIONS, not
   *  (option, language) rows, so the tab pages on options. */
  getEffectiveGroupedList(
    queryParams?: Record<string, string | number | boolean>,
  ): Observable<IResponseModel<IEffectiveGroup[]>> {
    return this.http.get<IResponseModel<IEffectiveGroup[]>>(
      this.apiUrl.getEffectiveGrouped,
      { params: queryParams },
    );
  }

  /** Creates a whole option and every language it ships with in one call
   *  (`POST /effective/option`) — 201 with the created group, 409 when the
   *  option code already exists. */
  createEffectiveOption(
    param: IEffectiveGroupCreateBody,
  ): Observable<IResponseModel<IEffectiveGroup>> {
    return this.http.post<IResponseModel<IEffectiveGroup>>(
      this.apiUrl.createEffectiveOption,
      param,
    );
  }

  /** Replaces an option's translations and/or flips its status
   *  (`PUT /effective/option/{option}`). The option code addresses the group
   *  and is never in the body — it is immutable. Restoring a soft-deleted
   *  option is this call with `useFlag: 'Y'`. */
  updateEffectiveOption(
    option: string,
    param: IEffectiveGroupUpdateBody,
  ): Observable<IResponseModel<IEffectiveGroup>> {
    const url = this.apiUrl.updateAndDeleteEffectiveOption.replace(
      "{option}",
      encodeURIComponent(String(option)),
    );
    return this.http.put<IResponseModel<IEffectiveGroup>>(url, param);
  }

  /** Two-stage, like the row-level delete but for the whole option: the first
   *  DELETE soft-deletes every language of the option (restorable by PUTting
   *  `useFlag: 'Y'`), calling it again on an already-deleted option removes it
   *  permanently. */
  deleteEffectiveOption(option: string): Observable<IResponseModel<null>> {
    const url = this.apiUrl.updateAndDeleteEffectiveOption.replace(
      "{option}",
      encodeURIComponent(String(option)),
    );
    return this.http.delete<IResponseModel<null>>(url);
  }

  getBadgeColors(
    container = "contents",
  ): Observable<IResponseModel<IBlobItem[]>> {
    return this.http
      .get<IResponseModel<(IBlobItem & { prefix?: string })[]>>(
        this.apiUrl.getBlobPublicList,
        {
          params: new HttpParams()
            .set("container", container)
            .set("prefix", "enricher/assets/badge_colour"),
        },
      )
      .pipe(
        map((response) => ({
          ...response,
          data: Array.isArray(response.data)
            ? response.data
                .map((item) => this.toPublicBlobItem(item))
                .filter((item): item is IBlobItem => item !== null)
                .filter((item) => BADGE_COLOR_ALLOWLIST.has(item.id))
            : [],
        })),
      );
  }

  getBlobIcons(
    prefix: string,
    container = "contents",
  ): Observable<IResponseModel<IBlobItem[]>> {
    const categoryPrefix = this.getIconCategoryPrefix(prefix);

    return this.http
      .get<IResponseModel<(IBlobItem & { prefix?: string })[]>>(
        this.apiUrl.getBlobPublicList,
        {
          params: new HttpParams()
            .set("container", container)
            .set("prefix", "enricher/assets/iconui_my3"),
        },
      )
      .pipe(
        map((response) => ({
          ...response,
          data: Array.isArray(response.data)
            ? response.data
                .map((item) => this.toPublicBlobItem(item))
                .filter((item): item is IBlobItem => item !== null)
                .filter(
                  (item) =>
                    !categoryPrefix || item.id.startsWith(categoryPrefix),
                )
            : [],
        })),
      );
  }

  private toPublicBlobItem(
    item: (IBlobItem & { prefix?: string }) | null | undefined,
  ): IBlobItem | null {
    if (!item?.url) {
      return null;
    }

    const id = item.id?.trim() || this.getBlobNameWithoutExtension(item.prefix);

    if (!id) {
      return null;
    }

    return {
      id,
      url: item.url.trim(),
    };
  }

  private getBlobNameWithoutExtension(prefix?: string): string {
    if (!prefix) {
      return "";
    }

    const fileName = prefix.split("/").pop()?.trim() ?? "";
    return fileName.replace(/\.[^/.]+$/, "");
  }

  private getIconCategoryPrefix(prefix: string): string {
    const normalized = prefix.trim().toLowerCase();
    const category = normalized
      .replace(/^emoji-/, "")
      .replace(/^iconui[_-]/, "")
      .replaceAll("-", "_");

    return category ? `iconui_${category}_` : "";
  }

  getMasterCatalogType(): Observable<IResponseModel<IMasterCatalogType[]>> {
    return this.http.get<IResponseModel<IMasterCatalogType[]>>(
      this.apiUrl.getCatalogType,
    );
  }

  getOfferringPagination(
    queryParams?: any,
    search?: string,
  ): Observable<IResponseModel> {
    if (search) {
      // Combine existing queryParams with search parameter
      const params = {
        ...queryParams,
        search: search,
      };
      return this.http.get<IResponseModel>(this.apiUrl.searchProductOfferings, {
        params: params,
      });
    }
    if (queryParams) {
      return this.http.get<IResponseModel>(
        this.apiUrl.getProductOfferingsPagination,
        {
          params: queryParams,
        },
      );
    }
    return this.http.get<IResponseModel>(
      this.apiUrl.getProductOfferingsPagination,
    );
  }

  // Permissions
  getPermissions(
    queryParams?: any,
  ): Observable<IResponseModel<IMasterPermission[]>> {
    return this.http.get<IResponseModel<IMasterPermission[]>>(
      this.apiUrl.getAndCreateMasterPermission,
      { params: queryParams },
    );
  }

  createPermission(param: any): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.getAndCreateMasterPermission,
      param,
    );
  }

  updatePermission(
    permissionId: string,
    param: any,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteMasterPermission}`.replace(
      "{permissionId}",
      encodeURIComponent(permissionId),
    );
    return this.http.put<IResponseModel<any>>(url, param);
  }

  deletePermission(permissionId: string): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteMasterPermission}`.replace(
      "{permissionId}",
      encodeURIComponent(permissionId),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  // Scopes crud
  getScopes(queryParams?: any): Observable<IResponseModel<IMasterScope[]>> {
    return this.http.get<IResponseModel<IMasterScope[]>>(
      this.apiUrl.getAndCreateMasterScope,
      {
        params: queryParams,
      },
    );
  }

  createScopes(param: any): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.getAndCreateMasterScope,
      param,
    );
  }

  updateScopes(scopeId: string, param: any): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteScopes}`.replace(
      "{scopeId}",
      encodeURIComponent(scopeId),
    );
    return this.http.put<IResponseModel<any>>(url, param);
  }

  deleteScopes(scopeId: string): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteScopes}`.replace(
      "{scopeId}",
      encodeURIComponent(scopeId),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  /** The users holding one scope. */
  getScopeUsers(scopeId: number): Observable<IResponseModel<IScopeUser[]>> {
    const url = this.apiUrl.scopeUsers.replace(
      "{scopeId}",
      encodeURIComponent(String(scopeId)),
    );
    return this.http.get<IResponseModel<IScopeUser[]>>(url);
  }

  /** One page of the active users a scope can be bound to. */
  getScopeUserOptions(params: {
    search?: string;
    page: number;
    pageSize: number;
  }): Observable<
    IResponseModel<{
      items: IScopeUser[];
      pagination: { totalItems: number; totalPages: number };
    }>
  > {
    const query: Record<string, string | number> = {
      page: params.page,
      pageSize: params.pageSize,
    };
    if (params.search?.trim()) query["search"] = params.search.trim();
    return this.http.get<
      IResponseModel<{
        items: IScopeUser[];
        pagination: { totalItems: number; totalPages: number };
      }>
    >(this.apiUrl.scopeUserOptions, { params: query });
  }

  /** Binds (add) or unbinds (remove) every user to/from every scope. Only
   *  the listed pairs change; a user's other scopes stay as they are. */
  assignScopeUsers(body: {
    scopeIds: number[];
    userIds: string[];
    action: ScopeAssignAction;
  }): Observable<IResponseModel<IScopeAssignResult>> {
    return this.http.post<IResponseModel<IScopeAssignResult>>(
      this.apiUrl.assignScopeUsers,
      body,
    );
  }

  // Languages crud (master catalog backing ENR_MST_LANGUAGE).
  getLanguages(
    queryParams?: any,
  ): Observable<IResponseModel<IMasterLanguage[]>> {
    return this.http.get<IResponseModel<IMasterLanguage[]>>(
      this.apiUrl.getAndCreateLanguage,
      {
        params: queryParams,
      },
    );
  }

  createLanguage(param: any): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.getAndCreateLanguage,
      param,
    );
  }

  updateLanguage(
    languageId: string,
    param: any,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteLanguage}`.replace(
      "{languageId}",
      encodeURIComponent(languageId),
    );
    return this.http.put<IResponseModel<any>>(url, param);
  }

  deleteLanguage(languageId: string): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.updateAndDeleteLanguage}`.replace(
      "{languageId}",
      encodeURIComponent(languageId),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  // Sync Fields crud — same envelope, flags and two-stage delete as the
  // language master.
  getSyncFields(
    queryParams?: Record<string, string | number | boolean>,
  ): Observable<IResponseModel<IMasterSyncField[]>> {
    return this.http.get<IResponseModel<IMasterSyncField[]>>(
      this.apiUrl.getAndCreateSyncField,
      { params: queryParams },
    );
  }

  createSyncField(body: ISyncFieldSaveBody): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.getAndCreateSyncField,
      body,
    );
  }

  /** An omitted property keeps its value — `{useFlag}` alone flips the
   *  status, `{useFlag: "Y"}` restores a deleted row. */
  updateSyncField(
    fieldId: string,
    body: Partial<ISyncFieldSaveBody>,
  ): Observable<IResponseModel<any>> {
    const url = this.apiUrl.updateAndDeleteSyncField.replace(
      "{fieldId}",
      encodeURIComponent(fieldId),
    );
    return this.http.put<IResponseModel<any>>(url, body);
  }

  /** Sets the order of the whole list: every non-deleted field id, in
   *  order. 409 when the list changed meanwhile. */
  reorderSyncFields(
    ids: number[],
  ): Observable<IResponseModel<{ ids: number[] }>> {
    return this.http.put<IResponseModel<{ ids: number[] }>>(
      this.apiUrl.syncFieldOrder,
      { ids },
    );
  }

  /** The keys the next segment of a Sync Field path can be, under `path`
   *  (the segments typed so far; "" = the price itself). 400 for a prefix
   *  the price cannot have. */
  getSyncPathFields(
    path: string,
    docType = "ProductOfferingPrice",
  ): Observable<ISyncPathFields> {
    return this.http
      .get<IResponseModel<ISyncPathFields>>(this.apiUrl.syncFieldPathFields, {
        params: { docType, path },
      })
      .pipe(map((res) => res.data as ISyncPathFields));
  }

  /** Whether `path` can be saved as a Sync Field — always 200; the row
   *  being edited is left out of the duplicate check (`excludeId`). */
  checkSyncPath(
    path: string,
    excludeId?: number | null,
    docType = "ProductOfferingPrice",
  ): Observable<ISyncPathCheck> {
    const params: Record<string, string> = { docType, path };
    if (excludeId != null) params["excludeId"] = String(excludeId);
    return this.http
      .get<IResponseModel<ISyncPathCheck>>(this.apiUrl.syncFieldCheckPath, {
        params,
      })
      .pipe(map((res) => res.data as ISyncPathCheck));
  }

  deleteSyncField(fieldId: string): Observable<IResponseModel<any>> {
    const url = this.apiUrl.updateAndDeleteSyncField.replace(
      "{fieldId}",
      encodeURIComponent(fieldId),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  // Auto Enrich Fields crud — the Sync Fields envelope, flags and
  // two-stage delete.
  getAutoEnrichFields(
    queryParams?: Record<string, string | number | boolean>,
  ): Observable<IResponseModel<IMasterAutoEnrichField[]>> {
    return this.http.get<IResponseModel<IMasterAutoEnrichField[]>>(
      this.apiUrl.getAndCreateAutoEnrichField,
      { params: queryParams },
    );
  }

  createAutoEnrichField(
    body: IAutoEnrichFieldSaveBody,
  ): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.getAndCreateAutoEnrichField,
      body,
    );
  }

  /** An omitted property keeps its value — `{useFlag}` alone flips the
   *  status, `{useFlag: "Y"}` restores a deleted row. */
  updateAutoEnrichField(
    fieldId: string,
    body: Partial<IAutoEnrichFieldSaveBody>,
  ): Observable<IResponseModel<any>> {
    const url = this.apiUrl.updateAndDeleteAutoEnrichField.replace(
      "{fieldId}",
      encodeURIComponent(fieldId),
    );
    return this.http.put<IResponseModel<any>>(url, body);
  }

  /** The keys the next segment of a property path can be, under `path`
   *  ("" = the document itself) on the offering or on a price (`level`) —
   *  the Sync Field path-fields shape.
   *  Not yet served by the BFF on every environment — the form treats a
   *  failure as "no suggestions" / "check unavailable" and never blocks
   *  Save on it. */
  getPropertyPathFields(
    path: string,
    level: AutoEnrichDocumentLevel,
  ): Observable<ISyncPathFields> {
    return this.http
      .get<IResponseModel<ISyncPathFields>>(
        this.apiUrl.autoEnrichFieldPathFields,
        { params: { level, path } },
      )
      .pipe(map((res) => res.data as ISyncPathFields));
  }

  /** The characteristic names seen on live offerings or prices, with how
   *  many documents carry each.
   *  Not yet served by the BFF on every environment — the form treats a
   *  failure as "no suggestions" / "check unavailable" and never blocks
   *  Save on it. */
  getPropertySourceNames(
    sourceType: AutoEnrichSourceType,
  ): Observable<IPropertySourceName[]> {
    return this.http
      .get<IResponseModel<IPropertySourceName[]>>(
        this.apiUrl.autoEnrichFieldSourceNames,
        { params: { sourceType } },
      )
      .pipe(map((res) => res.data as IPropertySourceName[]));
  }

  /** Whether a property's source can be saved — always 200; the row being
   *  edited is left out (`excludeId`).
   *  Not yet served by the BFF on every environment — the form treats a
   *  failure as "no suggestions" / "check unavailable" and never blocks
   *  Save on it. */
  checkPropertySource(
    sourceType: AutoEnrichSourceType,
    sourceName: string,
    excludeId?: number | null,
  ): Observable<ISyncPathCheck> {
    const params: Record<string, string> = { sourceType, sourceName };
    if (excludeId != null) params["excludeId"] = String(excludeId);
    return this.http
      .get<IResponseModel<ISyncPathCheck>>(
        this.apiUrl.autoEnrichFieldCheckSource,
        { params },
      )
      .pipe(map((res) => res.data as ISyncPathCheck));
  }

  /** Soft-deletes; a row already deleted is removed for good. */
  deleteAutoEnrichField(fieldId: string): Observable<IResponseModel<any>> {
    const url = this.apiUrl.updateAndDeleteAutoEnrichField.replace(
      "{fieldId}",
      encodeURIComponent(fieldId),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  // Resources
  getResources(
    queryParams?: any,
  ): Observable<IResponseModel<IMasterResource[]>> {
    return this.http.get<IResponseModel<IMasterResource[]>>(
      this.apiUrl.getResources,
      { params: queryParams },
    );
  }

  createResource(param: any): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(this.apiUrl.getResources, param);
  }

  updateResource(
    resourceId: string,
    param: any,
  ): Observable<IResponseModel<any>> {
    const url = this.apiUrl.updateAndDeleteResource.replace(
      "{resourceId}",
      encodeURIComponent(resourceId),
    );
    return this.http.put<IResponseModel<any>>(url, param);
  }

  deleteResource(resourceId: string): Observable<IResponseModel<any>> {
    const url = this.apiUrl.updateAndDeleteResource.replace(
      "{resourceId}",
      encodeURIComponent(resourceId),
    );
    return this.http.delete<IResponseModel<any>>(url);
  }

  //Audit Logs
  /** @deprecated Role-change history only (`/auditlog`, ENR_AUDIT_ROLE_CHANGE).
   *  The Audit Logs screen reads `/auditlog-system` through AuditLogsService;
   *  remove once the BFF retires `/auditlog`. */
  getAuditLogs(queryParams?: any): Observable<IAuditLogResponse> {
    return this.http.get<IAuditLogResponse>(this.apiUrl.getAuditLog, {
      params: queryParams,
    });
  }

  /** @deprecated See getAuditLogs. */
  getAuditLogById(auditId: string): Observable<IAuditLogResponse> {
    const url = this.apiUrl.getAuditLogById.replace(
      "{auditId}",
      encodeURIComponent(auditId),
    );
    return this.http.get<IAuditLogResponse>(url);
  }

  getMstFormComponent(): Observable<IResponseModel<FormDisplayItem[]>> {
    return this.http.get<IResponseModel<FormDisplayItem[]>>(
      this.apiUrl.mstFormComponentEndPoint,
    );
  }

  createMstFormComponent(
    data: FormDisplayItem,
  ): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.mstFormComponentEndPoint,
      data,
    );
  }

  updateMstFormComponent(
    id: number,
    payload: any,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.mstFormComponentEndPoint}/${encodeURIComponent(id)}`;
    return this.http.put<IResponseModel<any>>(url, payload);
  }

  deleteMstFormComponent(id: number): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.mstFormComponentEndPoint}/${encodeURIComponent(id)}`;
    return this.http.delete<IResponseModel<any>>(url);
  }

  getMstSegment(): Observable<IResponseModel<SegmentItem[]>> {
    return this.http.get<IResponseModel<SegmentItem[]>>(
      this.apiUrl.mstSegmentEndPoint,
    );
  }

  createMstSegment(data: SegmentItem): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.mstSegmentEndPoint,
      data,
    );
  }

  updateMstSegment(
    id: number,
    payload: SegmentItem,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.mstSegmentEndPoint}/${encodeURIComponent(id)}`;
    return this.http.put<IResponseModel<any>>(url, payload);
  }

  deleteMstSegment(id: number): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.mstSegmentEndPoint}/${encodeURIComponent(id)}`;
    return this.http.delete<IResponseModel<any>>(url);
  }

  getMstNType(): Observable<IResponseModel<NtypeItem[]>> {
    return this.http.get<IResponseModel<NtypeItem[]>>(
      this.apiUrl.mstNTypeEndPoint,
    );
  }

  createMstNType(data: NtypeItem): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.mstNTypeEndPoint,
      data,
    );
  }

  updateMstNType(
    id: number,
    payload: NtypeItem,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.mstNTypeEndPoint}/${encodeURIComponent(id)}`;
    return this.http.put<IResponseModel<any>>(url, payload);
  }

  deleteMstNType(id: number): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.mstNTypeEndPoint}/${encodeURIComponent(id)}`;
    return this.http.delete<IResponseModel<any>>(url);
  }

  getCfgChannel(): Observable<IResponseModel<BannerCfgChannel[]>> {
    return this.http.get<IResponseModel<BannerCfgChannel[]>>(
      this.apiUrl.cfgChannelEndPoint,
    );
  }

  getCfgPositionScope(): Observable<IResponseModel<PositionScopeItem[]>> {
    return this.http.get<IResponseModel<PositionScopeItem[]>>(
      this.apiUrl.cfgPositionScopeEndPoint,
    );
  }

  createCfgPositionScope(
    data: PositionScopeItem,
  ): Observable<IResponseModel<any>> {
    const createPayload = { ...data };
    delete createPayload.positionId;

    return this.http.post<IResponseModel<any>>(
      this.apiUrl.cfgPositionScopeEndPoint,
      createPayload,
    );
  }

  updateCfgPositionScope(
    id: number,
    payload: PositionScopeItem,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.cfgPositionScopeEndPoint}/${encodeURIComponent(id)}`;
    return this.http.put<IResponseModel<any>>(url, payload);
  }

  deleteCfgPositionScope(id: number): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.cfgPositionScopeEndPoint}/${encodeURIComponent(id)}`;
    return this.http.delete<IResponseModel<any>>(url);
  }

  getCfgFormComponent(filters?: {
    positionId?: number | null;
    formId?: number | null;
  }): Observable<IResponseModel<CFGFormItem[]>> {
    let params = new HttpParams();

    if (filters?.positionId != null) {
      params = params.set("positionId", String(filters.positionId));
    }

    if (filters?.formId != null) {
      params = params.set("formId", String(filters.formId));
    }

    return this.http.get<IResponseModel<CFGFormItem[]>>(
      this.apiUrl.cfgFormComponentEndPoint,
      { params },
    );
  }

  createCfgFormComponent(data: CFGFormItem): Observable<IResponseModel<any>> {
    return this.http.post<IResponseModel<any>>(
      this.apiUrl.cfgFormComponentEndPoint,
      data,
    );
  }

  updateCfgFormComponent(
    id: number,
    payload: CFGFormItem,
  ): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.cfgFormComponentEndPoint}/${encodeURIComponent(id)}`;
    return this.http.put<IResponseModel<any>>(url, payload);
  }

  deleteCfgFormComponent(id: number): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.cfgFormComponentEndPoint}/${encodeURIComponent(id)}`;
    return this.http.delete<IResponseModel<any>>(url);
  }

  getAccessRequest(): Observable<IResponseModel<AccessRequestItem[]>> {
    return this.http.get<IResponseModel<AccessRequestItem[]>>(
      this.apiUrl.accessRequestEndPoint,
    );
  }

  getAllCfgCampaigns(
    requested?: BannerCfgCampaignQuery,
  ): Observable<BannerCfgCampaignListResponse> {
    const query = requested ?? {
      limit: BANNER_CAMPAIGN_DEFAULT_LIMIT,
      offset: 0,
    };
    if (
      !Number.isInteger(query.limit) ||
      query.limit < 1 ||
      query.limit > BANNER_CAMPAIGN_MAX_LIMIT ||
      !Number.isSafeInteger(query.offset) ||
      query.offset < 0
    ) {
      throw new RangeError(
        "Campaign pagination requires limit 1-50 and a non-negative integer offset",
      );
    }
    let params = new HttpParams()
      .set("limit", query.limit)
      .set("offset", query.offset);
    if (query.search?.trim())
      params = params.set("search", query.search.trim());
    if (query.channelId !== undefined)
      params = params.set("channelId", query.channelId);
    if (query.positionId !== undefined)
      params = params.set("positionId", query.positionId);
    if (query.useFlag !== undefined)
      params = params.set("useFlag", query.useFlag);
    return this.http.get<BannerCfgCampaignListResponse>(
      this.apiUrl.cfgCampaignEndPoint,
      { params },
    );
  }

  getCfgCampaign(id: number): Observable<IResponseModel<BannerCfgCampaign>> {
    return this.http.get<IResponseModel<BannerCfgCampaign>>(
      `${this.apiUrl.cfgCampaignEndPoint}/${encodeURIComponent(id)}`,
    );
  }

  createCfgCampaign(
    payload: BannerCfgCampaignInput,
  ): Observable<IResponseModel<BannerCfgCampaign>> {
    return this.http.post<IResponseModel<BannerCfgCampaign>>(
      this.apiUrl.cfgCampaignEndPoint,
      payload,
    );
  }

  updateCfgCampaign(
    id: number,
    payload: BannerCfgCampaignInput,
  ): Observable<IResponseModel<BannerCfgCampaign>> {
    return this.http.put<IResponseModel<BannerCfgCampaign>>(
      `${this.apiUrl.cfgCampaignEndPoint}/${encodeURIComponent(id)}`,
      payload,
    );
  }

  deleteCfgCampaign(id: number): Observable<IResponseModel<null>> {
    return this.http.delete<IResponseModel<null>>(
      `${this.apiUrl.cfgCampaignEndPoint}/${encodeURIComponent(id)}`,
    );
  }

  deleteManyCfgCampaign(
    ids: readonly number[],
  ): Observable<IResponseModel<BannerCfgCampaignDeleteResult>> {
    return this.http.post<IResponseModel<BannerCfgCampaignDeleteResult>>(
      `${this.apiUrl.cfgCampaignEndPoint}/delete`,
      { ids },
    );
  }

  deleteAccessRequest(id: number): Observable<IResponseModel<any>> {
    const url = `${this.apiUrl.accessRequestEndPoint}/${encodeURIComponent(id)}`;
    return this.http.delete<IResponseModel<any>>(url);
  }
}
